import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import "./AdminPage.scss";
import { useLanguage } from "../../context/LanguageContext";
import AdminNav from "./AdminNav";
import AdminHomeTab from "./tabs/AdminHomeTab";
import useAdminAnalytics from "./hooks/useAdminAnalytics";
import useAdminBookings from "./hooks/useAdminBookings";
import useAdminCustomers from "./hooks/useAdminCustomers";
import useAdminDashboard from "./hooks/useAdminDashboard";
import useAdminEvents from "./hooks/useAdminEvents";
import useAdminInventory from "./hooks/useAdminInventory";
import useAdminLogs from "./hooks/useAdminLogs";
import useAdminPartnersLocations from "./hooks/useAdminPartnersLocations";
import useAdminPayments from "./hooks/useAdminPayments";
import useAdminPromoCodes from "./hooks/useAdminPromoCodes";
import useAdminRefunds from "./hooks/useAdminRefunds";
import useAdminSettings from "./hooks/useAdminSettings";
import AdminLogDrawer from "./AdminLogDrawer";
import AdminRefundDrawer from "./AdminRefundDrawer";
import AdminLocPartnersTab from "./tabs/AdminLocPartnersTab";
import AdminBookingsTab from "./tabs/AdminBookingsTab";
import AdminEventsTab from "./tabs/AdminEventsTab";
import AdminInventoryTab from "./tabs/AdminInventoryTab";
import AdminLogsTab from "./tabs/AdminLogsTab";
import AdminPaymentsTab from "./tabs/AdminPaymentsTab";
import AdminCustomersTab from "./tabs/AdminCustomersTab";
import AdminAnalyticsTab from "./tabs/AdminAnalyticsTab";
import AdminSettingsTab from "./tabs/AdminSettingsTab";
import {
  buildAuditCsv,
  formatIsoDate,
  isGuid,
  mapAdminAuditEntry,
} from "./adminUtils";
import { getAdminAudit } from "../../api/adminAudit";
import { exportAdminAnalytics, getAdminAnalytics } from "../../api/adminAnalytics";

const toggleSortDir = (current) =>
  current === "none" ? "desc" : current === "desc" ? "asc" : "none";
const toggleSortDirRequired = (current) => (current === "desc" ? "asc" : "desc");

const AdminPage = () => {
  const { t, language } = useLanguage();
  const layoutRef = useRef(null);
  const navRef = useRef(null);
  const navUpdateRef = useRef(() => {});
  const alertsRef = useRef(null);
  const partnersRef = useRef(null);
  const locationsRef = useRef(null);
  const bookingsRef = useRef(null);

  const [activeTab, setActiveTab] = useState("home");
  const [auditLog, setAuditLog] = useState([]);
  const auditTake = 40;
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [logSearch, setLogSearch] = useState("");
  const [logDateFrom, setLogDateFrom] = useState("");
  const [logDateTo, setLogDateTo] = useState("");
  const [logCategoryFilter, setLogCategoryFilter] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const logPageSize = 10;
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [selectedLogEntry, setSelectedLogEntry] = useState(null);
  const [focusedAuditTarget, setFocusedAuditTarget] = useState(null);
  const [analyticsRangePreset, setAnalyticsRangePreset] = useState("last30Days");
  const [analyticsCustomFrom, setAnalyticsCustomFrom] = useState(formatIsoDate(new Date()));
  const [analyticsCustomTo, setAnalyticsCustomTo] = useState(formatIsoDate(new Date()));
  const [analyticsGranularity, setAnalyticsGranularity] = useState("day");
  const [analyticsLocationId, setAnalyticsLocationId] = useState("all");
  const [analyticsProductTypeId, setAnalyticsProductTypeId] = useState("all");
  const [analyticsCompareEnabled, setAnalyticsCompareEnabled] = useState(false);
  const [analyticsCompareFrom, setAnalyticsCompareFrom] = useState(formatIsoDate(new Date()));
  const [analyticsCompareTo, setAnalyticsCompareTo] = useState(formatIsoDate(new Date()));
  const [analyticsSubTab, setAnalyticsSubTab] = useState("revenue");
  const [analyticsRankTab] = useState("location");
  const [analyticsReport, setAnalyticsReport] = useState(null);
  const [analyticsCompareReport, setAnalyticsCompareReport] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");

  const [, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);
  const auditFocusTimer = useRef(null);

  const showFeedback = useCallback((message, tone = "info") => {
    setFeedback({ message, tone });
    if (feedbackTimer.current) {
      clearTimeout(feedbackTimer.current);
    }
    feedbackTimer.current = setTimeout(() => {
      setFeedback(null);
    }, 3200);
  }, []);

  const refreshAudit = async ({ take = auditTake, silent = false } = {}) => {
    if (!silent) setAuditLoading(true);
    setAuditError("");
    try {
      const payload = await getAdminAudit({ take });
      const items = Array.isArray(payload) ? payload.map(mapAdminAuditEntry) : [];
      setAuditLog(items);
    } catch (error) {
      setAuditError(error?.message || "Failed to load audit log.");
      setAuditLog([]);
    } finally {
      if (!silent) setAuditLoading(false);
    }
  };


  const focusAuditTarget = (target) => {
    setFocusedAuditTarget(target);
    if (auditFocusTimer.current) {
      clearTimeout(auditFocusTimer.current);
    }
    auditFocusTimer.current = setTimeout(() => {
      setFocusedAuditTarget(null);
    }, 4000);
  };

  const {
    dashboard,
    dashboardLoading,
    dashboardError,
    dashboardCurrency,
    glanceCards,
    alertCards,
    refreshDashboard,
  } = useAdminDashboard({ t });
  const {
    partners,
    partnersLoading,
    partnersError,
    expandedPartnerId,
    setExpandedPartnerId,
    partnerDetailsById,
    partnerDetailsLoadingId,
    partnerCommissionEdits,
    partnerPinLoadingId,
    partnerFormOpen,
    setPartnerFormOpen,
    partnerEditingId,
    setPartnerEditingId,
    partnerForm,
    setPartnerForm,
    isSavingPartner,
    revealedPins,
    locations,
    setLocations,
    locationsLoading,
    locationsError,
    locationFormOpen,
    setLocationFormOpen,
    locationEditingId,
    setLocationEditingId,
    locationForm,
    setLocationForm,
    isSavingLocation,
    openPartnerForm,
    handlePartnerFormChange,
    handlePartnerCommissionChange,
    handlePartnerCommissionSave,
    handleGeneratePartnerPin,
    handlePartnerSave,
    handlePartnerEdit,
    handlePartnerDetailsToggle,
    handlePartnerResetPin,
    handlePartnerAccessToggle,
    handlePartnerDelete,
    openLocationForm,
    handleLocationFormChange,
    handleLocationSave,
    handleLocationEdit,
    handleLocationToggleActive,
    handleLocationDelete: handleLocationDeleteBase,
    handleLocationsRefresh,
  } = useAdminPartnersLocations({ showFeedback, refreshAudit, setActiveTab });
  const {
    selectedEquipmentTypeId,
    setSelectedEquipmentTypeId,
    equipmentAdjustments,
    equipmentBlockForms,
    equipmentSavingId,
    equipmentBlockActionId,
    equipmentSnapshot,
    selectedEquipment,
    handleEquipmentAdjustmentChange,
    handleEquipmentAvailableChange,
    handleEquipmentSave,
    handleEquipmentBlockFormChange,
    handleEquipmentBlockCreate,
    handleEquipmentBlockDelete,
    clearLocationAdjustments,
  } = useAdminInventory({
    dashboard,
    locations,
    setLocations,
    showFeedback,
    refreshDashboard,
  });
  const {
    events,
    eventsLoading,
    eventsError,
    eventFormOpen,
    setEventFormOpen,
    eventEditingId,
    setEventEditingId,
    eventForm,
    setEventForm,
    eventSaving,
    expandedEventId,
    setExpandedEventId,
    eventActionId,
    eventTemplates,
    templatesLoading,
    selectedTemplateId,
    setSelectedTemplateId,
    imageUploading,
    attendeeForms,
    attendeeActionId,
    attendeeEditingId,
    attendeeEditForm,
    openEventForm,
    handleEventEdit,
    handleEventDuplicate,
    handleEventFormChange,
    handleApplyTemplate,
    handleSaveTemplateFromForm,
    handleUpdateSelectedTemplate,
    handleDeleteSelectedTemplate,
    handleEventImageUpload,
    handleEventSave,
    handleEventDelete,
    handleAttendeeFormChange,
    handleAddAttendee,
    handleRemoveAttendee,
    handleAttendeeEdit,
    handleAttendeeEditChange,
    handleAttendeeEditCancel,
    handleAttendeeUpdate,
  } = useAdminEvents({ locations, equipmentSnapshot, showFeedback });
  const {
    rewardVouchers,
    rewardVouchersLoading,
    rewardVouchersError,
    rewardVoucherActionId,
    updateRewardVoucherStatus,
  } = useAdminPromoCodes({ showFeedback });
  const handleLocationDelete = (location) =>
    handleLocationDeleteBase(location, clearLocationAdjustments);
  const {
    bookings,
    setBookings,
    bookingsTotal,
    bookingsLoading,
    bookingsError,
    bookingsPage,
    setBookingsPage,
    bookingsPageSize,
    searchQuery,
    setSearchQuery,
    setAppliedQuery,
    statusFilter,
    setStatusFilter,
    locationFilter,
    setLocationFilter,
    sortTimeDir,
    setSortTimeDir,
    sortPriceDir,
    setSortPriceDir,
    issuesOnly,
    setIssuesOnly,
    expandedBookingId,
    bookingDetailsById,
    bookingDetailsLoadingId,
    correctionBookingId,
    setCorrectionBookingId,
    correctionNote,
    setCorrectionNote,
    isSavingNote,
    bookingTokenActionId,
    statusUpdatingId,
    issueUpdatingId,
    bookingStatusOptions,
    bookingStatusChoices,
    bookingLocationOptions,
    totalBookingsPages,
    handleSearch,
    handleResetFilters,
    handleBookingDetailsToggle,
    handleManualCorrection,
    handleCorrectionSubmit,
    handleStatusOverride,
    handleIssueUpdate,
    handleResendBookingToken,
    handleReissueBookingToken,
    handleRevokeBookingToken,
  } = useAdminBookings({ t, locations, showFeedback, refreshAudit });
  const {
    moneyMonth,
    setMoneyMonth,
    moneyYear,
    setMoneyYear,
    moneyMetric,
    setMoneyMetric,
    moneyLoading,
    moneyError,
    moneyMonthOptions,
    moneyYearOptions,
    moneyChartData,
    moneyMetricKey,
    moneyChartStyle,
    moneyMetricLabel,
    moneyRangeLabel,
    moneyMetricTotal,
    moneyCurrency,
    moneyHasData,
    moneySummaryCards,
    payments,
    paymentsLoading,
    paymentsError,
    paymentsPage,
    setPaymentsPage,
    paymentsPageSize,
    expandedPaymentRowId,
    paymentSearchQuery,
    setPaymentSearchQuery,
    paymentAppliedQuery,
    paymentStatusFilter,
    setPaymentStatusFilter,
    paymentTypeFilter,
    setPaymentTypeFilter,
    paymentStatusOptions,
    paymentSortTimeDir,
    setPaymentSortTimeDir,
    paymentSortAmountDir,
    setPaymentSortAmountDir,
    handlePaymentSearch,
    handleResetPaymentFilters,
    handlePaymentRowToggle,
    handleExportMonthlyRevenue,
    handleExportVatReady,
    handleExportAccountantCsv,
  } = useAdminPayments({ t, dashboardCurrency, showFeedback });
  const {
    settingsData,
    settingsLoading,
    settingsError,
    settingsSavingSection,
    settingsSaveState,
    settingsActiveSection,
    setSettingsActiveSection,
    settingsHighlightId,
    settingsDirtySections,
    updateSettingsValue,
    handleSettingsSave,
    handleSettingsCancel,
    handleSettingsTestAction,
    handleSettingsResetAction,
  } = useAdminSettings({ showFeedback, refreshAudit, equipmentSnapshot });
  const {
    customersTotal,
    customersPage,
    setCustomersPage,
    customersLoading,
    customersError,
    customerSearchQuery,
    setCustomerSearchQuery,
    customerSortBy,
    setCustomerSortBy,
    customerSortDir,
    setCustomerSortDir,
    expandedCustomerKey,
    customerActionId,
    customerBlockReasons,
    customerProfiles,
    totalCustomerPages,
    handleCustomerSearch,
    handleToggleCustomer,
    handleCustomerReasonChange,
    handleToggleBlockCustomer,
    handleSaveBlockReason,
    handleCustomerManualBooking,
    handleResendCustomerConfirmation,
  } = useAdminCustomers({ showFeedback, refreshAudit });
  const {
    refundDrawerOpen,
    refundBookingId,
    refundForm,
    setRefundForm,
    refundBookingLoading,
    refundBookingError,
    isRefunding,
    openRefundDrawer,
    closeRefundDrawer,
    handleRefundSubmit,
    selectedBooking,
    refundableAmount,
    refundAmountValue,
    refundFeeEstimate,
    refundNet,
    refundRequiresNote,
    refundHistory,
    selectedCurrency,
  } = useAdminRefunds({
    bookings,
    setBookings,
    showFeedback,
    refreshAudit,
  });
  const {
    logCategoryOptions,
    filteredLogEntries,
  } = useAdminLogs({
    auditLog,
    partners,
    locations,
    logSearch,
    logDateFrom,
    logDateTo,
    logCategoryFilter,
    logLocationFilter: "all",
    logPartnerFilter: "all",
  });
  const logPageCount = useMemo(
    () => Math.max(1, Math.ceil(filteredLogEntries.length / logPageSize)),
    [filteredLogEntries.length, logPageSize]
  );
  const pagedLogEntries = useMemo(() => {
    const start = (logPage - 1) * logPageSize;
    return filteredLogEntries.slice(start, start + logPageSize);
  }, [filteredLogEntries, logPage, logPageSize]);

  useEffect(() => {
    setLogPage(1);
  }, [
    logSearch,
    logDateFrom,
    logDateTo,
    logCategoryFilter,
  ]);

  useEffect(() => {
    setLogPage((prev) => Math.min(prev, logPageCount));
  }, [logPageCount]);
  const {
    analyticsRange,
    analyticsCompareRange,
    analyticsFilters,
    analyticsCurrency,
    analyticsCompareActive,
    analyticsChartData,
    analyticsBookingsChartData,
    analyticsHasData,
    analyticsKpis,
    analyticsUsageChartData,
    analyticsUsageByEquipment,
    analyticsHeatmapSlots,
    analyticsHeatmapDays,
    analyticsHeatmapMap,
    analyticsHeatmapMax,
    analyticsAttendanceSeries,
    analyticsAttendanceTotals,
    analyticsFunnel,
    analyticsRefundReasons,
    analyticsPartnerReliability,
    analyticsLateReturns,
    analyticsNoShows,
    analyticsPickupDelay,
    analyticsLocationOptions,
    analyticsProductTypeOptions,
    analyticsRankRows,
    analyticsRankMax,
    analyticsRangeLabel,
    buildSparkline,
  } = useAdminAnalytics({
    analyticsRangePreset,
    analyticsCustomFrom,
    analyticsCustomTo,
    analyticsCompareEnabled,
    analyticsCompareFrom,
    analyticsCompareTo,
    setAnalyticsCompareFrom,
    setAnalyticsCompareTo,
    analyticsLocationId,
    analyticsProductTypeId,
    analyticsGranularity,
    analyticsRankTab,
    analyticsReport,
    analyticsCompareReport,
    locations,
    dashboardCurrency,
  });
  const downloadBlob = (blob, fileName, fallback) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      fileName ||
      fallback ||
      `wayli-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    return () => {
      if (feedbackTimer.current) {
        clearTimeout(feedbackTimer.current);
      }
      if (auditFocusTimer.current) {
        clearTimeout(auditFocusTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!layoutRef.current || !navRef.current) {
      return;
    }

    const state = { current: 0, target: 0 };
    let animationFrame = null;
    let ticking = null;

    const updateTarget = () => {
      if (!layoutRef.current || !navRef.current) {
        return;
      }
      const layoutRect = layoutRef.current.getBoundingClientRect();
      const navRect = navRef.current.getBoundingClientRect();
      const navHeight = navRect.height;
      const margin = 0;
      const topOffset = 0;

      let clampTop = layoutRect.top + margin;
      let clampBottom = layoutRect.bottom - margin - navHeight;

      if (clampBottom < clampTop) {
        const middle = (clampTop + clampBottom) / 2;
        clampTop = middle;
        clampBottom = middle;
      }

      const desiredTop = Math.min(Math.max(topOffset, clampTop), clampBottom);
      const currentTop = navRect.top;
      const delta = desiredTop - currentTop;

      state.target = state.current + delta;
    };

    const smoothStep = () => {
      if (!navRef.current) {
        return;
      }
      state.current += (state.target - state.current) * 0.1;
      if (Math.abs(state.target - state.current) < 0.15) {
        state.current = state.target;
      }
      navRef.current.style.setProperty("--admin-nav-shift", `${state.current}px`);
      animationFrame = window.requestAnimationFrame(smoothStep);
    };

    const scheduleUpdate = () => {
      if (ticking != null) {
        return;
      }
      ticking = window.requestAnimationFrame(() => {
        ticking = null;
        updateTarget();
      });
    };

    smoothStep();
    scheduleUpdate();

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    navUpdateRef.current = scheduleUpdate;

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (ticking) {
        window.cancelAnimationFrame(ticking);
      }
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      navUpdateRef.current = () => {};
    };
  }, []);

  useEffect(() => {
    navUpdateRef.current?.();
  }, [activeTab]);

  useEffect(() => {
    refreshAudit({ take: auditTake });
  }, [auditTake]);

  useEffect(() => {
    let cancelled = false;

    const loadAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError("");
      try {
        const payload = await getAdminAnalytics({
          from: analyticsRange.start,
          to: analyticsRange.end,
          interval: analyticsGranularity,
          ...analyticsFilters,
        });
        if (cancelled) return;
        setAnalyticsReport(payload);
      } catch (error) {
        if (cancelled) return;
        setAnalyticsError(error?.message || "Failed to load analytics.");
        setAnalyticsReport(null);
      } finally {
        if (!cancelled) setAnalyticsLoading(false);
      }
    };

    loadAnalytics();

    return () => {
      cancelled = true;
    };
  }, [
    analyticsFilters,
    analyticsGranularity,
    analyticsRange.end,
    analyticsRange.start,
  ]);

  useEffect(() => {
    if (!analyticsCompareEnabled || !analyticsCompareRange) {
      setAnalyticsCompareReport(null);
      return;
    }

    let cancelled = false;

    const loadCompare = async () => {
      try {
        const payload = await getAdminAnalytics({
          from: analyticsCompareRange.start,
          to: analyticsCompareRange.end,
          interval: analyticsGranularity,
          ...analyticsFilters,
        });
        if (cancelled) return;
        setAnalyticsCompareReport(payload);
      } catch (error) {
        if (cancelled) return;
        showFeedback(error?.message || "Failed to load compare range.", "warning");
        setAnalyticsCompareReport(null);
      }
    };

    loadCompare();

    return () => {
      cancelled = true;
    };
  }, [
    analyticsCompareEnabled,
    analyticsCompareRange,
    analyticsFilters,
    analyticsGranularity,
    showFeedback,
  ]);

  const toggleLogCategory = (categoryId) => {
    setLogCategoryFilter((prev) =>
      prev.includes(categoryId)
        ? prev.filter((item) => item !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleOpenLogDrawer = (entry) => {
    setSelectedLogEntry(entry);
    setLogDrawerOpen(true);
    if (entry?.target) {
      focusAuditTarget(entry.target);
    }
  };

  const handleCloseLogDrawer = () => {
    setSelectedLogEntry(null);
    setLogDrawerOpen(false);
  };

  const handleExportLogs = (format) => {
    if (filteredLogEntries.length === 0) {
      showFeedback("No logs to export.", "warning");
      return;
    }
    if (format === "json") {
      const blob = new Blob([JSON.stringify(filteredLogEntries, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `wayli-logs-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showFeedback("Logs exported as JSON.", "success");
      return;
    }
    const csv = buildAuditCsv(filteredLogEntries);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `wayli-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showFeedback("Logs exported as CSV.", "success");
  };

  const handleLogResetFilters = () => {
    setLogSearch("");
    setLogDateFrom("");
    setLogDateTo("");
    setLogCategoryFilter([]);
    showFeedback("Log filters reset.", "info");
  };

  const handleOpenBookingFromLog = (entry) => {
    if (!entry?.target || !isGuid(entry.target)) return;
    setSearchQuery(entry.target);
    setAppliedQuery(entry.target);
    setActiveTab("bookings");
  };

  const handleOpenPartnerFromLog = (entry) => {
    const partnerMatch = partners.find(
      (partner) => partner.name && partner.name.toLowerCase() === entry.actor.toLowerCase()
    );
    if (!partnerMatch) return;
    setActiveTab("loc-partners");
    setExpandedPartnerId(partnerMatch.id);
  };

  const handleExportAnalytics = async (format) => {
    try {
      const { blob, fileName } = await exportAdminAnalytics({
        from: analyticsRange.start,
        to: analyticsRange.end,
        interval: analyticsGranularity,
        format,
        ...analyticsFilters,
      });
      downloadBlob(blob, fileName, `wayli-analytics.${format}`);
      showFeedback("Analytics export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export analytics.", "warning");
    }
  };

  const handleExportAnalyticsAccountant = () => {
    showFeedback("Accountant exports are available in the Money tab.", "info");
  };

  const handleAnalyticsChartClick = (payload) => {
    if (!payload?.activeLabel) return;
    showFeedback(`Selected ${payload.activeLabel}.`, "info");
  };

  return (
    <div className="admin-page">
      <Header />
      <main className="admin-page__content">
       
        <div className="admin-shell" ref={layoutRef}>
          <AdminNav
            t={t}
            activeTab={activeTab}
            onSelectTab={setActiveTab}
            navRef={navRef}
          />
          <div className="admin-body">
            <AdminHomeTab
              t={t}
              isActive={activeTab === "home"}
              dashboardLoading={dashboardLoading}
              dashboardError={dashboardError}
              glanceCards={glanceCards}
              alertCards={alertCards}
              alertsRef={alertsRef}
            />
                        <AdminLocPartnersTab
              t={t}
              isActive={activeTab === "loc-partners"}
              partnersRef={partnersRef}
              partnerFormOpen={partnerFormOpen}
              handlePartnerSave={handlePartnerSave}
              partnerForm={partnerForm}
              handlePartnerFormChange={handlePartnerFormChange}
              partnerEditingId={partnerEditingId}
              handleGeneratePartnerPin={handleGeneratePartnerPin}
              isSavingPartner={isSavingPartner}
              setPartnerFormOpen={setPartnerFormOpen}
              setPartnerEditingId={setPartnerEditingId}
              setPartnerForm={setPartnerForm}
              locations={locations}
              partnersLoading={partnersLoading}
              partnersError={partnersError}
              partners={partners}
              partnerDetailsById={partnerDetailsById}
              partnerDetailsLoadingId={partnerDetailsLoadingId}
              partnerCommissionEdits={partnerCommissionEdits}
              expandedPartnerId={expandedPartnerId}
              handlePartnerDetailsToggle={handlePartnerDetailsToggle}
              handlePartnerEdit={handlePartnerEdit}
              handlePartnerAccessToggle={handlePartnerAccessToggle}
              handlePartnerDelete={handlePartnerDelete}
              handlePartnerCommissionChange={handlePartnerCommissionChange}
              handlePartnerCommissionSave={handlePartnerCommissionSave}
              partnerPinLoadingId={partnerPinLoadingId}
              revealedPins={revealedPins}
              handlePartnerResetPin={handlePartnerResetPin}
              openPartnerForm={openPartnerForm}
              locationsRef={locationsRef}
              locationFormOpen={locationFormOpen}
              handleLocationSave={handleLocationSave}
              locationForm={locationForm}
              handleLocationFormChange={handleLocationFormChange}
              isSavingLocation={isSavingLocation}
              locationEditingId={locationEditingId}
              setLocationFormOpen={setLocationFormOpen}
              setLocationEditingId={setLocationEditingId}
              setLocationForm={setLocationForm}
              locationsLoading={locationsLoading}
              locationsError={locationsError}
              handleLocationEdit={handleLocationEdit}
              handleLocationToggleActive={handleLocationToggleActive}
              handleLocationDelete={handleLocationDelete}
              openLocationForm={openLocationForm}
              handleLocationsRefresh={handleLocationsRefresh}
            />
            <AdminBookingsTab
              t={t}
              isActive={activeTab === "bookings"}
              bookingsRef={bookingsRef}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleSearch={handleSearch}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              setBookingsPage={setBookingsPage}
              bookingStatusOptions={bookingStatusOptions}
              locationFilter={locationFilter}
              setLocationFilter={setLocationFilter}
              bookingLocationOptions={bookingLocationOptions}
              sortTimeDir={sortTimeDir}
              setSortTimeDir={setSortTimeDir}
              toggleSortDirRequired={toggleSortDirRequired}
              sortPriceDir={sortPriceDir}
              setSortPriceDir={setSortPriceDir}
              toggleSortDir={toggleSortDir}
              issuesOnly={issuesOnly}
              setIssuesOnly={setIssuesOnly}
              handleResetFilters={handleResetFilters}
              bookingsLoading={bookingsLoading}
              bookingsError={bookingsError}
              bookings={bookings}
              bookingDetailsById={bookingDetailsById}
              bookingDetailsLoadingId={bookingDetailsLoadingId}
              expandedBookingId={expandedBookingId}
              handleBookingDetailsToggle={handleBookingDetailsToggle}
              openRefundDrawer={openRefundDrawer}
              isRefunding={isRefunding}
              refundBookingId={refundBookingId}
              bookingStatusChoices={bookingStatusChoices}
              handleStatusOverride={handleStatusOverride}
              statusUpdatingId={statusUpdatingId}
              issueUpdatingId={issueUpdatingId}
              handleIssueUpdate={handleIssueUpdate}
              handleManualCorrection={handleManualCorrection}
              correctionBookingId={correctionBookingId}
              handleCorrectionSubmit={handleCorrectionSubmit}
              correctionNote={correctionNote}
              setCorrectionNote={setCorrectionNote}
              isSavingNote={isSavingNote}
              setCorrectionBookingId={setCorrectionBookingId}
              handleResendBookingToken={handleResendBookingToken}
              handleReissueBookingToken={handleReissueBookingToken}
              handleRevokeBookingToken={handleRevokeBookingToken}
              bookingTokenActionId={bookingTokenActionId}
              bookingsTotal={bookingsTotal}
              bookingsPage={bookingsPage}
              bookingsPageSize={bookingsPageSize}
              totalBookingsPages={totalBookingsPages}
            />
            <AdminEventsTab
              t={t}
              language={language}
              isActive={activeTab === "events"}
              locations={locations}
              equipmentSnapshot={equipmentSnapshot}
              events={events}
              eventsLoading={eventsLoading}
              eventsError={eventsError}
              eventFormOpen={eventFormOpen}
              eventEditingId={eventEditingId}
              eventForm={eventForm}
              eventSaving={eventSaving}
              expandedEventId={expandedEventId}
              eventActionId={eventActionId}
              eventTemplates={eventTemplates}
              templatesLoading={templatesLoading}
              selectedTemplateId={selectedTemplateId}
              setSelectedTemplateId={setSelectedTemplateId}
              imageUploading={imageUploading}
              attendeeForms={attendeeForms}
              attendeeActionId={attendeeActionId}
              attendeeEditingId={attendeeEditingId}
              attendeeEditForm={attendeeEditForm}
              setEventFormOpen={setEventFormOpen}
              setEventEditingId={setEventEditingId}
              setEventForm={setEventForm}
              setExpandedEventId={setExpandedEventId}
              openEventForm={openEventForm}
              handleEventEdit={handleEventEdit}
              handleEventDuplicate={handleEventDuplicate}
              handleEventFormChange={handleEventFormChange}
              handleApplyTemplate={handleApplyTemplate}
              handleSaveTemplateFromForm={handleSaveTemplateFromForm}
              handleUpdateSelectedTemplate={handleUpdateSelectedTemplate}
              handleDeleteSelectedTemplate={handleDeleteSelectedTemplate}
              handleEventImageUpload={handleEventImageUpload}
              handleEventSave={handleEventSave}
              handleEventDelete={handleEventDelete}
              handleAttendeeFormChange={handleAttendeeFormChange}
              handleAddAttendee={handleAddAttendee}
              handleRemoveAttendee={handleRemoveAttendee}
              handleAttendeeEdit={handleAttendeeEdit}
              handleAttendeeEditChange={handleAttendeeEditChange}
              handleAttendeeEditCancel={handleAttendeeEditCancel}
              handleAttendeeUpdate={handleAttendeeUpdate}
            />
                        <AdminInventoryTab
              t={t}
              isActive={activeTab === "inventory"}
              dashboardLoading={dashboardLoading}
              dashboardError={dashboardError}
              equipmentSnapshot={equipmentSnapshot}
              selectedEquipmentTypeId={selectedEquipmentTypeId}
              setSelectedEquipmentTypeId={setSelectedEquipmentTypeId}
              selectedEquipment={selectedEquipment}
              locationsLoading={locationsLoading}
              locationsError={locationsError}
              locations={locations}
              equipmentAdjustments={equipmentAdjustments}
              equipmentBlockForms={equipmentBlockForms}
              handleEquipmentAdjustmentChange={handleEquipmentAdjustmentChange}
              handleEquipmentAvailableChange={handleEquipmentAvailableChange}
              handleEquipmentSave={handleEquipmentSave}
              handleEquipmentBlockFormChange={handleEquipmentBlockFormChange}
              handleEquipmentBlockCreate={handleEquipmentBlockCreate}
              handleEquipmentBlockDelete={handleEquipmentBlockDelete}
              equipmentSavingId={equipmentSavingId}
              equipmentBlockActionId={equipmentBlockActionId}
            />
            <AdminLogsTab
              isActive={activeTab === "logs"}
              logSearch={logSearch}
              setLogSearch={setLogSearch}
              logDateFrom={logDateFrom}
              setLogDateFrom={setLogDateFrom}
              logDateTo={logDateTo}
              setLogDateTo={setLogDateTo}
              logCategoryOptions={logCategoryOptions}
              logCategoryFilter={logCategoryFilter}
              toggleLogCategory={toggleLogCategory}
              handleExportLogs={handleExportLogs}
              handleLogResetFilters={handleLogResetFilters}
              filteredLogEntries={filteredLogEntries}
              pagedLogEntries={pagedLogEntries}
              logPage={logPage}
              logPageCount={logPageCount}
              setLogPage={setLogPage}
              focusedAuditTarget={focusedAuditTarget}
              handleOpenLogDrawer={handleOpenLogDrawer}
              auditLoading={auditLoading}
              auditError={auditError}
            />
            <AdminPaymentsTab
              t={t}
              isActive={activeTab === "payments"}
              moneyLoading={moneyLoading}
              moneyError={moneyError}
              moneySummaryCards={moneySummaryCards}
              moneyMonth={moneyMonth}
              setMoneyMonth={setMoneyMonth}
              moneyMonthOptions={moneyMonthOptions}
              moneyYear={moneyYear}
              setMoneyYear={setMoneyYear}
              moneyYearOptions={moneyYearOptions}
              moneyMetric={moneyMetric}
              setMoneyMetric={setMoneyMetric}
              moneyMetricLabel={moneyMetricLabel}
              moneyRangeLabel={moneyRangeLabel}
              moneyMetricTotal={moneyMetricTotal}
              moneyCurrency={moneyCurrency}
              moneyHasData={moneyHasData}
              moneyChartData={moneyChartData}
              moneyMetricKey={moneyMetricKey}
              moneyChartStyle={moneyChartStyle}
              handleExportMonthlyRevenue={handleExportMonthlyRevenue}
              handleExportVatReady={handleExportVatReady}
              handleExportAccountantCsv={handleExportAccountantCsv}
              paymentSearchQuery={paymentSearchQuery}
              setPaymentSearchQuery={setPaymentSearchQuery}
              handlePaymentSearch={handlePaymentSearch}
              paymentStatusFilter={paymentStatusFilter}
              setPaymentStatusFilter={setPaymentStatusFilter}
              paymentTypeFilter={paymentTypeFilter}
              setPaymentTypeFilter={setPaymentTypeFilter}
              paymentStatusOptions={paymentStatusOptions}
              paymentSortTimeDir={paymentSortTimeDir}
              setPaymentSortTimeDir={setPaymentSortTimeDir}
              paymentSortAmountDir={paymentSortAmountDir}
              setPaymentSortAmountDir={setPaymentSortAmountDir}
              handleResetPaymentFilters={handleResetPaymentFilters}
              paymentsLoading={paymentsLoading}
              paymentsError={paymentsError}
              payments={payments}
              paymentAppliedQuery={paymentAppliedQuery}
              expandedPaymentRowId={expandedPaymentRowId}
              handlePaymentRowToggle={handlePaymentRowToggle}
              paymentsPage={paymentsPage}
              setPaymentsPage={setPaymentsPage}
              paymentsPageSize={paymentsPageSize}
              toggleSortDirRequired={toggleSortDirRequired}
              toggleSortDir={toggleSortDir}
            />
            <AdminCustomersTab
              t={t}
              isActive={activeTab === "customers"}
              customersTotal={customersTotal}
              customerSearchQuery={customerSearchQuery}
              setCustomerSearchQuery={setCustomerSearchQuery}
              handleCustomerSearch={handleCustomerSearch}
              customerSortBy={customerSortBy}
              setCustomerSortBy={setCustomerSortBy}
              customerSortDir={customerSortDir}
              setCustomerSortDir={setCustomerSortDir}
              customersLoading={customersLoading}
              customersError={customersError}
              customerProfiles={customerProfiles}
              expandedCustomerKey={expandedCustomerKey}
              handleToggleCustomer={handleToggleCustomer}
              customerBlockReasons={customerBlockReasons}
              customerActionId={customerActionId}
              handleCustomerReasonChange={handleCustomerReasonChange}
              handleToggleBlockCustomer={handleToggleBlockCustomer}
              handleSaveBlockReason={handleSaveBlockReason}
              handleCustomerManualBooking={handleCustomerManualBooking}
              handleResendCustomerConfirmation={handleResendCustomerConfirmation}
              customersPage={customersPage}
              setCustomersPage={setCustomersPage}
              totalCustomerPages={totalCustomerPages}
            />
            <AdminAnalyticsTab
              t={t}
              isActive={activeTab === "analytics"}
              analyticsRangePreset={analyticsRangePreset}
              setAnalyticsRangePreset={setAnalyticsRangePreset}
              analyticsCustomFrom={analyticsCustomFrom}
              setAnalyticsCustomFrom={setAnalyticsCustomFrom}
              analyticsCustomTo={analyticsCustomTo}
              setAnalyticsCustomTo={setAnalyticsCustomTo}
              analyticsGranularity={analyticsGranularity}
              setAnalyticsGranularity={setAnalyticsGranularity}
              analyticsLocationId={analyticsLocationId}
              setAnalyticsLocationId={setAnalyticsLocationId}
              analyticsLocationOptions={analyticsLocationOptions}
              analyticsProductTypeId={analyticsProductTypeId}
              setAnalyticsProductTypeId={setAnalyticsProductTypeId}
              analyticsProductTypeOptions={analyticsProductTypeOptions}
              analyticsCompareEnabled={analyticsCompareEnabled}
              setAnalyticsCompareEnabled={setAnalyticsCompareEnabled}
              analyticsCompareFrom={analyticsCompareFrom}
              setAnalyticsCompareFrom={setAnalyticsCompareFrom}
              analyticsCompareTo={analyticsCompareTo}
              setAnalyticsCompareTo={setAnalyticsCompareTo}
              analyticsSubTab={analyticsSubTab}
              setAnalyticsSubTab={setAnalyticsSubTab}
              analyticsRankTab={analyticsRankTab}
              analyticsRankRows={analyticsRankRows}
              analyticsRankMax={analyticsRankMax}
              analyticsRangeLabel={analyticsRangeLabel}
              analyticsLoading={analyticsLoading}
              analyticsError={analyticsError}
              analyticsHasData={analyticsHasData}
              analyticsKpis={analyticsKpis}
              analyticsChartData={analyticsChartData}
              analyticsBookingsChartData={analyticsBookingsChartData}
              analyticsCompareActive={analyticsCompareActive}
              analyticsCurrency={analyticsCurrency}
              analyticsUsageChartData={analyticsUsageChartData}
              analyticsUsageByEquipment={analyticsUsageByEquipment}
              analyticsHeatmapSlots={analyticsHeatmapSlots}
              analyticsHeatmapDays={analyticsHeatmapDays}
              analyticsHeatmapMap={analyticsHeatmapMap}
              analyticsHeatmapMax={analyticsHeatmapMax}
              analyticsAttendanceSeries={analyticsAttendanceSeries}
              analyticsAttendanceTotals={analyticsAttendanceTotals}
              analyticsFunnel={analyticsFunnel}
              analyticsRefundReasons={analyticsRefundReasons}
              analyticsPartnerReliability={analyticsPartnerReliability}
              analyticsLateReturns={analyticsLateReturns}
              analyticsNoShows={analyticsNoShows}
              analyticsPickupDelay={analyticsPickupDelay}
              handleExportAnalytics={handleExportAnalytics}
              handleExportAnalyticsAccountant={handleExportAnalyticsAccountant}
              handleAnalyticsChartClick={handleAnalyticsChartClick}
              buildSparkline={buildSparkline}
            />
            <AdminSettingsTab
              isActive={activeTab === "settings"}
              settingsData={settingsData}
              settingsLoading={settingsLoading}
              settingsError={settingsError}
              settingsSavingSection={settingsSavingSection}
              settingsSaveState={settingsSaveState}
              settingsActiveSection={settingsActiveSection}
              setSettingsActiveSection={setSettingsActiveSection}
              settingsHighlightId={settingsHighlightId}
              settingsDirtySections={settingsDirtySections}
              updateSettingsValue={updateSettingsValue}
              handleSettingsSave={handleSettingsSave}
              handleSettingsCancel={handleSettingsCancel}
              handleSettingsTestAction={handleSettingsTestAction}
              handleSettingsResetAction={handleSettingsResetAction}
              showFeedback={showFeedback}
              refreshAudit={refreshAudit}
              rewardVouchers={rewardVouchers}
              rewardVouchersLoading={rewardVouchersLoading}
              rewardVouchersError={rewardVouchersError}
              rewardVoucherActionId={rewardVoucherActionId}
              updateRewardVoucherStatus={updateRewardVoucherStatus}
              equipmentSnapshot={equipmentSnapshot}
            />
            <AdminRefundDrawer
              t={t}
              refundDrawerOpen={refundDrawerOpen}
              closeRefundDrawer={closeRefundDrawer}
              selectedBooking={selectedBooking}
              selectedCurrency={selectedCurrency}
              refundForm={refundForm}
              setRefundForm={setRefundForm}
              refundBookingLoading={refundBookingLoading}
              refundBookingError={refundBookingError}
              refundableAmount={refundableAmount}
              refundAmountValue={refundAmountValue}
              refundFeeEstimate={refundFeeEstimate}
              refundNet={refundNet}
              refundRequiresNote={refundRequiresNote}
              handleRefundSubmit={handleRefundSubmit}
              isRefunding={isRefunding}
              refundHistory={refundHistory}
            />
                  <AdminLogDrawer
              t={t}
              logDrawerOpen={logDrawerOpen}
              handleCloseLogDrawer={handleCloseLogDrawer}
              selectedLogEntry={selectedLogEntry}
              handleOpenBookingFromLog={handleOpenBookingFromLog}
              handleOpenPartnerFromLog={handleOpenPartnerFromLog}
            />
        </div>
      </div>
    </main>
    <Footer />
  </div>
  );
};

export default AdminPage;
